import type { Alignment, Config, Driver, DriveStep, Popover, Side } from 'driver.js';
import { PrefKey } from '../../../../../_ui-core_/base/package/api/package/pam/pam-domain';
import { I18n } from '../../../../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { isDesktopWidth } from '../../../../../_ui-core_/base/package/util/device';
import { localStorage_get } from '../../../../../_ui-core_/base/package/util/localStorage';
import { storePreferences } from '../../../../../_ui-core_/base/package/util/storePreferences';
import { LOCAL_STORAGE_KEY_ONBOARDING_OVERRIDE } from '../../../../../constants';
import { type UserState, UserStates } from '../activation-flow/activation-flow-domain';
import { LoginStatus } from '../login/login-domain';

export const ElementId = {
  MY_PROMOTIONS: 'my-promotions',
  BONUSES: 'activated-campaigns',
  PAST_REWARDS: 'past-campaigns',
  HEADER_USER_ICON: 'header-user-icon',
  DEPOSIT_ICON: 'deposit-icon',
  GAMIFICATION_ICON_DESKTOP: 'gamification-icon-desktop',
  GAMIFICATION_ICON_MOBILE: 'gamification-icon-mobile',
  INCENTIVE_REWARDS: 'incentive-rewards',
  INCENTIVE_REWARDS_ACTIVE: 'incentive-rewards-active',
  INCENTIVE_REWARDS_PAST: 'incentive-rewards-past',
  INCENTIVE_MISSIONS: 'incentive-missions',
  INCENTIVE_MISSIONS_PAST: 'incentive-missions-past',
  INCENTIVE_TOURNAMENTS: 'incentive-tournaments',
  INCENTIVE_TOURNAMENTS_PAST: 'incentive-tournaments-past',
} as const;

export type ElementId = (typeof ElementId)[keyof typeof ElementId];

type OnboardingStep = DriveStep & { id: string };

type Flow = Config & {
  id: string;
  steps: OnboardingStep[];
};

export class OnboardingService {
  private readonly _flows: Flow[] = [];
  private readonly _registeredElements = new Map<ElementId, WeakRef<HTMLElement>>();
  private readonly _skipElementIds = new Set<ElementId>();
  private _shownFlows: Set<string> = new Set();
  private _driver: Driver | null = null;
  private _currentFlowId?: string;
  private _permanentlyInterruptedFlowId?: string;
  private _isOnboardingEnabled?: boolean;
  private _timeout?: ReturnType<typeof setTimeout>;
  private readonly _isOnboardingForced: boolean;
  private readonly _isOnboardingPrevented: boolean;
  private _loginStatus: LoginStatus = LoginStatus.UNDEFINED;

  private readonly _generatePopover = (
    title: string,
    description: string,
    align: Alignment = 'center',
    side: Side = 'bottom',
    customClass = '',
    doneBtnText = '',
  ): Popover => {
    return {
      title,
      description,
      align,
      side,
      popoverClass: customClass,
      doneBtnText: doneBtnText ? window.$app.$t.get(doneBtnText) : window.$app.$t.get(I18n.onboarding.doneBtnText2),
    };
  };

  private readonly _userStateSubscriptionCallback = (value: UserState) => {
    if (([UserStates.ACTIVATED, UserStates.IDLE] as readonly UserStates[]).includes(value.state)) {
      this._shownFlows = window.$app.loginStore.value.preferences?.onboardingFlowsShown || new Set();
      this._startFlowIfPossible();
    } else if (this._currentFlowId) {
      this._interruptFlowPermanently();
    }
  };

  private readonly _markFlowAsShown = (id: string) => {
    if (this._shownFlows.has(id)) return;
    this._shownFlows.add(id);
    this._storeShownFlows(Array.from(this._shownFlows));
  };

  constructor() {
    window.$app.logger.log('OnboardingService init');
    const stepsCollection = {
      base: {
        user: {
          id: ElementId.HEADER_USER_ICON,
          popover: this._generatePopover(
            I18n.onboarding.base.userIcon.title,
            I18n.onboarding.base.userIcon.description,
            'center',
            isDesktopWidth() ? 'right' : 'bottom',
          ),
          element: undefined,
        },
        deposit: {
          id: ElementId.DEPOSIT_ICON,
          popover: this._generatePopover(
            I18n.onboarding.base.depositIcon.title,
            I18n.onboarding.base.depositIcon.description,
            isDesktopWidth() ? 'start' : 'center',
            isDesktopWidth() ? 'right' : 'top',
          ),
          element: undefined,
        },
        gamification: {
          id: isDesktopWidth() ? ElementId.GAMIFICATION_ICON_DESKTOP : ElementId.GAMIFICATION_ICON_MOBILE,
          popover: this._generatePopover(
            I18n.onboarding.base.gamification.title,
            I18n.onboarding.base.gamification.description,
            'center',
            isDesktopWidth() ? 'right' : 'top',
            isDesktopWidth() ? '' : 'driver-popover--arrow-fix-gamification',
          ),
          element: undefined,
        },
      },
      promotions: {
        promo: {
          id: ElementId.MY_PROMOTIONS,
          popover: this._generatePopover(
            I18n.onboarding.promotions.promotions.title,
            I18n.onboarding.promotions.promotions.description,
            isDesktopWidth() ? 'center' : 'start',
            'bottom',
            isDesktopWidth() ? '' : 'driver-popover--arrow-fix-promotions',
          ),
          element: undefined,
        },
        bonuses: {
          id: ElementId.BONUSES,
          popover: this._generatePopover(
            I18n.onboarding.promotions.bonuses.title,
            I18n.onboarding.promotions.bonuses.description,
            'center',
            'bottom',
            isDesktopWidth() ? '' : 'driver-popover--arrow-fix-bonuses',
          ),
          element: undefined,
        },
        pastRewards: {
          id: ElementId.PAST_REWARDS,
          popover: this._generatePopover(
            I18n.onboarding.promotions.pastRewards.title,
            I18n.onboarding.promotions.pastRewards.description,
            isDesktopWidth() ? 'center' : 'start',
            'bottom',
            isDesktopWidth() ? '' : 'driver-popover--arrow-fix-past',
          ),
          element: undefined,
        },
      },
    };

    // Singleton — lives for app lifetime, no cleanup needed (accepted pattern exception)
    addEventListener('offline', () => {
      this._interruptFlowPermanently();
    });

    this._flows = [
      {
        id: 'base',
        steps: isDesktopWidth()
          ? [stepsCollection.base.deposit, stepsCollection.base.gamification, stepsCollection.base.user]
          : [stepsCollection.base.user, stepsCollection.base.deposit, stepsCollection.base.gamification],
      },
      {
        id: 'promotions',
        steps: [
          stepsCollection.promotions.promo,
          stepsCollection.promotions.bonuses,
          stepsCollection.promotions.pastRewards,
        ],
      },
    ];

    window.$app.activationFlow.subscribeUserState(this._userStateSubscriptionCallback);
    this._isOnboardingEnabled = window.$app.casinoConfig.onboardingEnabled;
    this._startFlowIfPossible();
    const onboardingOverride = localStorage_get<string>(LOCAL_STORAGE_KEY_ONBOARDING_OVERRIDE, '');
    this._isOnboardingForced = onboardingOverride === 'force';
    this._isOnboardingPrevented = onboardingOverride === 'prevent';
    this._initLoginStatus();

    // Subscribe to modal activity — defer/interrupt onboarding while modals are active
    window.$app.modal.onModalActivityChange((hasActiveModal) => {
      if (hasActiveModal && this._currentFlowId) {
        this._interruptFlow();
      } else if (!hasActiveModal) {
        this._startFlowIfPossible();
      }
    });
  }

  public registerElement(id: ElementId, element: HTMLElement) {
    window.$app.logger.log(`OnboardingService registerElement: '${id}'`);
    this._registeredElements.set(id, new WeakRef(element));
    this._startFlowIfPossible();
  }

  public unregisterElement(id: ElementId) {
    window.$app.logger.log(`OnboardingService unregisterElement: '${id}'`);
    this._registeredElements.delete(id);
    this._resetTimeout();
  }

  public hasElement(id: ElementId): boolean {
    const ref = this._registeredElements.get(id);
    if (!ref) return false;
    // Clean up stale WeakRefs
    if (!ref.deref()) {
      this._registeredElements.delete(id);
      return false;
    }
    return true;
  }

  public skipElement(id: ElementId) {
    if (this._skipElementIds.has(id)) return;
    window.$app.logger.log(`OnboardingService skipElement: '${id}'`);
    this._skipElementIds.add(id);
    this._startFlowIfPossible();
  }

  private _initLoginStatus() {
    window.$app.logger.log('OnboardingService _initLoginStatus');
    window.$app.login.getFinalLoginStatus().then((status) => {
      window.$app.logger.log(`OnboardingService _initLoginStatus '${status}'`);
      this._loginStatus = status;
    });
  }

  private _interruptFlow() {
    window.$app.logger.log(`OnboardingService _interruptFlow: temporarily interrupting flow '${this._currentFlowId}'`);
    this._resetTimeout();
    this._driver?.destroy();
  }

  private _interruptFlowPermanently() {
    window.$app.logger.log(
      `OnboardingService _interruptFlowPermanently: permanently interrupting flow '${this._currentFlowId}'`,
    );
    this._permanentlyInterruptedFlowId = this._currentFlowId;
    this._resetTimeout();
    this._driver?.destroy();
  }

  private _resetTimeout() {
    window.$app.logger.log(`OnboardingService _resetTimeout: clearing flow '${this._currentFlowId}'`);
    clearTimeout(this._timeout);
    this._timeout = undefined;
    this._currentFlowId = undefined;
  }

  private async _initDriver() {
    window.$app.logger.log('OnboardingService _initDriver');
    if (!this._driver) {
      // As the CSS link tags are prepended to the head, the order of the links
      // is reversed. This means that the onboarding theme CSS is loaded after
      // the driver CSS, which allows it to override driver styles if necessary.
      await this._loadOnboardingThemeCss();
      await this._loadDriverCss();
      window.$app.logger.log('OnboardingService _initDriver - CSS loaded');
      const { driver } = await import('driver.js');
      this._driver = driver({
        allowClose: false,
        stagePadding: isDesktopWidth() ? 5 : 15,
        showButtons: ['next'],
        nextBtnText: window.$app.$t.get(I18n.onboarding.nextBtnText),
        doneBtnText: window.$app.$t.get(I18n.onboarding.doneBtnText),
        onHighlighted: () => {
          if (this._currentFlowId) {
            this._markFlowAsShown(this._currentFlowId);
          }
        },
        onDestroyed: () => {
          window.$app.logger.log('OnboardingService driver onDestroyed — re-evaluating flows');
          this._resetTimeout();
          window.$app.activationFlow.unsubscribeUserState(this._userStateSubscriptionCallback);
          this._startFlowIfPossible();
        },
      });
    }
  }

  private async _storeShownFlows(flowIds: string[]): Promise<void> {
    window.$app.logger.log(`OnboardingService _storeShownFlows: [${flowIds.join(', ')}]`);
    await storePreferences(PrefKey.CASINO_ONBOARDING, flowIds.join(','));
  }

  private _canStartFlow(): boolean {
    if (this._loginStatus !== LoginStatus.LOGGED_IN) {
      window.$app.logger.log(
        `OnboardingService _startFlowIfPossible skipped due to login status: '${this._loginStatus}'`,
      );
      return false;
    }
    if (this._permanentlyInterruptedFlowId) {
      window.$app.logger.log(
        `OnboardingService _startFlowIfPossible skipped due to permanently interrupted flow: '${this._permanentlyInterruptedFlowId}'`,
      );
      return false;
    }
    if (window.$app.modal.hasActiveOrQueuedModal) {
      window.$app.logger.log('OnboardingService _startFlowIfPossible skipped due to active/queued modal');
      return false;
    }
    if (!this._isOnboardingForced) {
      if (
        this._isOnboardingPrevented ||
        !([UserStates.ACTIVATED, UserStates.IDLE] as readonly UserStates[]).includes(
          window.$app.activationFlow.getCurrentState(),
        )
      ) {
        window.$app.logger.log(
          `OnboardingService _startFlowIfPossible skipped due to onboarding prevented or inactive state: '${window.$app.activationFlow.getCurrentState()}'`,
        );
        return false;
      }
    }
    if (this._isOnboardingEnabled !== true) {
      window.$app.logger.log('OnboardingService _startFlowIfPossible skipped due to onboarding disabled');
      return false;
    }
    return true;
  }

  private _findNextFlow(): Flow | undefined {
    for (const flow of this._flows) {
      if (this._shownFlows?.has(flow.id) && !this._isOnboardingForced) {
        window.$app.logger.log(`OnboardingService _startFlowIfPossible skipping already shown flow: '${flow.id}'`);
        continue;
      }

      const missingElement = flow.steps.find(
        (step: OnboardingStep) => !this.hasElement(<ElementId>step.id) && !this._skipElementIds.has(<ElementId>step.id),
      );
      if (missingElement) {
        window.$app.logger.log(
          `OnboardingService _startFlowIfPossible cannot start flow '${flow.id}' yet, missing element: '${missingElement.id}'.`,
        );
        continue;
      }

      return flow;
    }
    return undefined;
  }

  private _startFlowIfPossible(): void {
    window.$app.logger.log('OnboardingService _startFlowIfPossible');

    if (this._currentFlowId || !this._canStartFlow()) {
      return;
    }

    const flow = this._findNextFlow();
    if (!flow) {
      return;
    }

    // Filter out steps whose elements are marked as skippable (e.g. gamification disabled)
    const activeSteps = flow.steps.filter((step: OnboardingStep) => !this._skipElementIds.has(<ElementId>step.id));
    if (activeSteps.length === 0) {
      // All steps skipped — mark flow as shown and re-evaluate
      this._markFlowAsShown(flow.id);
      return;
    }

    for (const step of activeSteps) {
      const el = this._registeredElements.get(<ElementId>step.id)?.deref();
      if (!el) return; // Element was GC'd, abort flow start
      step.element = el;
    }

    this._currentFlowId = flow.id;
    this._initDriver();

    this._timeout = setTimeout(() => {
      window.$app.logger.log(`OnboardingService _startFlowIfPossible starting flow: '${flow.id}'`);
      this._driver?.setSteps(activeSteps);
      this._driver?.drive();
    }, 1_000);
  }

  private _loadOnboardingThemeCss(): Promise<void> {
    window.$app.logger.log('OnboardingService _loadOnboardingThemeCss…');
    return new Promise((resolve, reject) => {
      if (document.getElementById('onboarding-theme-css')) {
        window.$app.logger.log('OnboardingService _loadOnboardingThemeCss skipped, already loaded');
        resolve();
        return;
      }

      const theme = window.$app.appConfig?.theme ?? 'de-games';
      const link = document.createElement('link');
      link.id = 'onboarding-theme-css';
      link.rel = 'stylesheet';
      link.href = `${window.location.origin}${window.$app.appConfig.staticUiHostAssetsUrl}/themes/${theme}/onboarding/onboarding-service.css`;
      link.onload = () => {
        window.$app.logger.log('OnboardingService _loadOnboardingThemeCss loaded');
        resolve();
      };
      link.onerror = () => {
        window.$app.logger.log(`OnboardingService _loadOnboardingThemeCss failed for theme '${theme}'`);
        reject(new Error(`Failed to load onboarding-service.css for theme '${theme}'`));
      };
      document.head.prepend(link);
    });
  }

  private _loadDriverCss(): Promise<void> {
    window.$app.logger.log('OnboardingService _loadDriverCss…');
    return new Promise((resolve, reject) => {
      if (document.getElementById('driver-css')) {
        window.$app.logger.log('OnboardingService _loadDriverCss skipped, already loaded');
        resolve();
        return;
      }

      const link = document.createElement('link');
      link.id = 'driver-css';
      link.rel = 'stylesheet';
      link.href = `${window.location.origin}${window.$app.appConfig.staticUiHostAssetsUrl}/vendor/driver.css`;
      link.onload = () => {
        window.$app.logger.log('OnboardingService _loadDriverCss loaded');
        resolve();
      };
      link.onerror = () => {
        window.$app.logger.log('OnboardingService _loadDriverCss failed to load');
        reject(new Error('Failed to load driver.css'));
      };
      document.head.prepend(link);
    });
  }
}
